import React from 'react';
import type { ProjektdatenAbweichung, ProjektdatenFeld } from './ufsaProjectData';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
    abweichungen: ProjektdatenAbweichung[];
    isReadOnly?: boolean;
    onUebernehmen: (feld: ProjektdatenFeld) => void;
}

/**
 * Hinweis „Projektdaten weichen von der gespeicherten Kopie ab" ueber den
 * Projektstammdaten der Unterlage. Bauherr und Verteiler werden nur beim
 * ERSTEN Oeffnen kopiert; hier sieht der Nutzer, was sich seither im Projekt
 * geaendert hat, und uebernimmt je Feld bzw. Liste per Klick. Nichts wird
 * beim Laden ueberschrieben.
 */
export const UfsaProjektdatenHinweis: React.FC<Props> = ({ abweichungen, isReadOnly = false, onUebernehmen }) => {
    if (!abweichungen.length) return null;
    return (
        <div role="status" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm">Projektdaten haben sich seit dem ersten Öffnen geändert</div>
                    <p className="text-xs mt-0.5">
                        Die Unterlage führt eine eigene Kopie von Bauherr und Beteiligten. Übernehmen Sie je Feld, was aus den Projektdaten gelten soll — die Kopie wird sonst nicht angefasst.
                    </p>
                </div>
            </div>
            <ul className="mt-3 space-y-2">
                {abweichungen.map(a => (
                    <li key={a.feld} className="bg-white/70 border border-amber-100 rounded-lg p-3 flex flex-col sm:flex-row sm:items-start gap-3">
                        <div className="min-w-0 flex-1 text-xs">
                            <div className="font-bold text-slate-800 text-sm">{a.titel}</div>
                            <div className="mt-1 text-slate-600">
                                <span className="font-medium text-slate-500">In der Unterlage:</span> <span className="break-words">{a.kopie}</span>
                            </div>
                            <div className="text-slate-600">
                                <span className="font-medium text-slate-500">Im Projekt:</span> <span className="break-words">{a.projekt}</span>
                            </div>
                            {a.details.map(d => <div key={d} className="text-slate-600 mt-0.5 break-words">{d}</div>)}
                        </div>
                        {!isReadOnly && (
                            <button
                                type="button"
                                onClick={() => onUebernehmen(a.feld)}
                                title={`${a.titel} aus den Projektdaten in die Unterlage übernehmen`}
                                className="shrink-0 px-2.5 py-1.5 text-xs font-bold text-amber-900 bg-white border border-amber-300 hover:bg-amber-100 rounded-md inline-flex items-center gap-1.5"
                            >
                                <RefreshCw className="w-3.5 h-3.5" /> Aus Projektdaten übernehmen
                            </button>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};
